import React, {useRef,useState} from "react";
import {StyleSheet, Text, TouchableOpacity} from "react-native";
import WebPageLayout from "../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../layouts/TablePageLayout";
import {CoreTable} from "../../../../components/CoreTable";
import {convertBase64toBlob} from "../../../../../../utils/helpers";
import {Entypo, MaterialCommunityIcons, MaterialIcons} from "@expo/vector-icons";
import {BaseFilters} from "../../../../../web/hooks/useFilters";
import useEntityListPage from "../../../../hooks/useEntityListPage";
import TicketsListScreenFilters from "./TicketsListScreenFilters";
import useUser from "../../../../../../hooks/useUser";
import {humanizeDate} from "../../../../../../utils/humanizers";
import {
  useLazyExportTicketsQuery,
  useLazyGetTicketsQuery,
  useUpdateTicketStatusMutation
} from "../../../../../../store/services/api/ticket-api";
import {PRIMARY_COLOUR} from "../../../../../../constants/GlobalStyle";
import {useModal} from "../../../../../../hooks/useModal";
import {ImageViewer} from "../../../../../web-v2/components/ImageViewer";
import {TicketViewModel} from "../../../../../../store/data/models/ticket-view-model";
import UpdateTicketModal from "../../../../components/modals/UpdateTicketModal";
import {useSetLoading} from "../../../../../../hooks/useSetLoading";
import {Button as AntButton, Tooltip} from "antd";
import CreateTicketModal from "../modals/CreateTicketModal";
import {ScreenProps} from "../../../../../../utils/screen-props-helper";
import {TicketsStackParamList} from "../../TicketsStack";
import WorkOrderManageModal from "../../../../../../components/modals/New/WorkOrderManageModal";
import TicketUpdatesListModal from "../modals/TicketUpdatesListModal"
import {ExportOutlined, PlusOutlined} from "@ant-design/icons";
import {SelectOption} from "../../../../../../store/data/models/select-option";
import {Animated} from "react-native";
import { Button } from "react-native-paper";
import { useExcelExport } from "../../../../hooks/useExport";
import ColumnExportModal, { ExportableColumn } from "../../../../../../components/modals/ColumnExportModal";
import { Card } from "antd";
import { TextAlignment } from "pdf-lib";
import useHasPermission from "../../../../../../hooks/useHasPermission";


interface TicketsListScreenFilters extends BaseFilters {
  ticketNumber?: string;
  startDate?: string;
  endDate?: string;
  ticketDescription?: string;
  contactEmail?: string;
  status?: string;
  entrySource?: string;
  hasUpdates?: boolean | '';
  buildingId?: string[];
  floorId?: string[];
  locationId?: string[];
  siteIds?: string[];
  teamIds?: string[];
  assetTypeId?: string[];
  subAssetTypeId?: string[];
  workOrderReference?: string;
  unassignedOnly?: boolean;
  createdByUserId?: string[];
}

export default function TicketsListScreen({navigation, route}: ScreenProps<TicketsStackParamList, 'list'>) {
  const routeFilters = route.params;

  const [updateTicketStatus] = useUpdateTicketStatusMutation();
  const [createTicketModalOpen, setCreateTicketModalOpen] = useState(false);
  const [workOrderModalVisible, setWorkOrderModalVisible] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | undefined>();
  const [selectedAsset, setSelectedAsset] = useState<SelectOption | undefined>();
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TicketViewModel | undefined>();

  const { exportExcel, isLoading: exportFetching } = useExcelExport({
  query: useLazyExportTicketsQuery
});

const [exportModalOpen, setExportModalOpen] = useState(false);
const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
const {user} = useUser();
const canEdit =  user?.role !== 'ViewOnly'


const exportableColumns: ExportableColumn[] = [
  { key: "Number", label: "Ticket Number" },
  { key: "LatestWorkOrderReference", label: "Work Order" },
  { key: "EntrySource", label: "Ticket Source" },
  { key: "Building", label: "Building" },
  { key: "Floor", label: "Floor" },
  { key: "Location", label: "Location" },
  { key: "SubAssetType", label: "Sub Asset Type" },
  { key: "Code", label: "Code" },
  { key: "FormattedDateSubmitted", label: "Date Submitted" },
  { key: "Description", label: "Ticket Description" },
  { key: "ContactEmail", label: "Contact Details" },
  { key: "Status", label: "Status" },
  { key: "FormattedDateOfStatusChange", label: "Date of status change" },
  { key: "FormattedClosedOn", label: "Closed On" },
  { key: "ClosedBy", label: "Closed By" },
  { key: "FormattedCreatedOn", label: "Created On" },
  { key: "Images", label: "Images" },
  { key: "Site", label: "Site" },
  { key: "Team", label: "Team" },
  { key: "Updates", label: "Updates" },
];



  useSetLoading([exportFetching]);

  const {
    navigateToEdit,
    onSubmitFilter,
    filter,
    tableData,
    isLoading,
  } = useEntityListPage<TicketsListScreenFilters>({
    entityName: "Ticket",
    lazyTableQuery: useLazyGetTicketsQuery,
    deleteEntityMutation: undefined,
    filterDefinition: {
      status: 'All',
      hasUpdates: '',
      sortedColumn: "createdOn",
      unassignedOnly: false,
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50
    },
    routeFilters: routeFilters
  });

  const {showModal, hideModal} = useModal();

  const handleOnCreateWorkOrderClick = (item) => {
    setSelectedTicketId(item.id);
    setSelectedAsset({
      value: item.assetId,
      label: item.code ?? item.description
    } as SelectOption)
    setWorkOrderModalVisible(true);
  }

  const handleOnWorkOrderClick = (item) => {
    if (item.latestWorkOrderId) {
      navigation.navigate('workOrders', {screen: 'recordView', params: {id: item.latestWorkOrderId}});
      return;
    }
  }

  const toggleTicketStatus = async (element) => {
    try {
      const newStatus = element.status === "Closed" ? "Open" : "Closed";
      await updateTicketStatus({id: element.id, status: newStatus});
    } catch (error) {
      console.error("Error changing ticket status:", error);
    }
  };

  const handleOnShowImagesModal = (images: string[]) => {
    showModal(
      <ImageViewer images={images} onClosed={hideModal}/>
    )
  }

  const [filtersVisible, setFiltersVisible] = useState(true);
    const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;
  
    const toggleFilters = () => {
      const toValue = filtersVisible ? 0 : 1;
      Animated.timing(filtersWidth, {
        toValue,
        duration: 300,
        useNativeDriver: false,
      }).start();
      setFiltersVisible(!filtersVisible);
    };

    const getRowStyle = (item: any) => {
      if (item.site === "NB") {
        return '#0f9ed5';
      }
      else if (item.site === "GC" && item.team !== "Infrastructure") {
        return "#96dcf8";
      }
    };
    const { hasPermission } = useHasPermission();
    const canCreateWorkOrders = hasPermission('CanCreateWorkOrders');

  return (
    <WebPageLayout
      header={{
        previousTab: {title: 'Dashboard', routeName: 'dashboard', replaceStack: true},
        tabs: [
          {title: 'Tickets', routeName: 'list'}
        ]
      }}
    >
      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: "hidden",
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], 
              }),
              height: "100%",
            }}
          >
            <TicketsListScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }
        actions={(
          <>
            <Button
              icon={filtersVisible ? "chevron-left" : "chevron-right"}
              mode="contained-tonal"
              onPress={toggleFilters}
              style={{ marginRight: 8 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>
            {canEdit && (
              <AntButton
              size='large'
              icon={<PlusOutlined />}
              onClick={() => setCreateTicketModalOpen(true)}
            >
              Create Ticket
            </AntButton>

            )}
            
            <AntButton
              size='large'
              icon={<ExportOutlined />}
              loading={exportFetching}
              onClick={() => setExportModalOpen(true)}
            >
              Export Excel
            </AntButton>
          </>
        )}
      >
        <CoreTable<any>
  isLoading={isLoading}
  headers={[
    { text: 'Edit', flex: 0.4 },
    { text: 'Ticket', sortedName: "number", flex: 0.6 },
    { text: 'Work Order', sortedName: "latestWorkOrderNumber", flex: 0.7 },
    { text: 'Team', sortedName: "team"},
    { text: 'Entry Source', sortedName: "entrySource" },
    { text: 'Building', sortedName: "building"},
    { text: 'Floor', sortedName: "floor" },
    { text: 'Location', sortedName: "location" },
    { text: 'Sub Asset Type', sortedName: "subAssetType"},
    { text: 'Code', sortedName: "code"},
    { text: 'Date Submitted', sortedName: "dateSubmitted" },
    { text: 'Ticket Description', sortedName: "description" },
    { text: 'Contact Details', sortedName: "contactEmail"},
    { text: 'Status', sortedName: "status" },
    { text: 'Closed On', sortedName: "closedOn"},
    { text: 'Closed By', sortedName: "closedBy" },
    { text: 'Created On', sortedName: "createdOn" },
    { text: 'Photos', flex: 0.7 },
    { text: 'Change Status', flex: 0.7 },
    { text: 'Updates', flex: 0.7 }
  ]}
  data={tableData}
  keyExtractor={(item) => item.id}
  renderItem={({ item, index, headers }) => (
    <CoreTable.Row key={index}>
      {headers.map((header, i) => {
        switch (header.text) {
          case 'Edit':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header  }}>
                {canEdit && <TouchableOpacity onPress={() => {
                  setSelectedTicketId(item.id);
                  setCreateTicketModalOpen(true);
                }}>
                  <Entypo name="pencil" size={24} color="black" />
                </TouchableOpacity>
                }
                
              </CoreTable.Cell>
            );
          case 'Ticket':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.number}</CoreTable.Cell>;
          case 'Work Order':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>
                { !item.latestWorkOrderId && item.status === 'Open' && item.site === "GC" && item.team === "Infrastructure" && canCreateWorkOrders? (
                  
                  <Text onPress={() => handleOnCreateWorkOrderClick(item)} style={{ fontWeight: 'bold' }}>Create</Text>
                ) : (
                  <Tooltip title={item.latestWorkOrderReference}>
                    <Text onPress={() => handleOnWorkOrderClick(item)}>{item.latestWorkOrderReference}</Text>
                  </Tooltip>
                )}
              </CoreTable.Cell>
            );
          case 'Team':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header, padding: 0,fontSize: 14,  flexWrap: 'wrap' ,overflowWrap: 'break-word', alignItems:'flex-center' ,wordBreak: 'break-word'  }}>
              <Card
                    bordered
                    style={{
                      backgroundColor: getRowStyle(item),
                      padding:0,
                      boxShadow: 'none',
                      borderRadius: 4,
                      textAlign:'center',
                    }}
                    bodyStyle={{ padding: 2, fontSize: 14, flexWrap: 'wrap',textAlign:'center',overflowWrap: 'break-word', wordBreak: 'break-word' }}
                  >
                    {item.teamFormatted}
                  </Card>
              
              </CoreTable.Cell>;
          case 'Entry Source':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.entrySource}</CoreTable.Cell>;
          case 'Building':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.building}</CoreTable.Cell>;
          case 'Floor':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.floor}</CoreTable.Cell>;
          case 'Location':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.location}</CoreTable.Cell>;
          case 'Sub Asset Type':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.subAssetType}</CoreTable.Cell>;
          case 'Code':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.code}</CoreTable.Cell>;
          case 'Date Submitted':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{humanizeDate(item.dateSubmitted)}</CoreTable.Cell>;
          case 'Ticket Description':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.description}</CoreTable.Cell>;
          case 'Contact Details':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.contactEmail}</CoreTable.Cell>;
          case 'Status':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.status}</CoreTable.Cell>;
          case 'Closed On':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{humanizeDate(item.closedOn, true)}</CoreTable.Cell>;
          case 'Closed By':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{item.closedBy ?? 'N/A'}</CoreTable.Cell>;
          case 'Created On':
            return <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>{humanizeDate(item.createdOn)}</CoreTable.Cell>;
          case 'Photos':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header  }}>
                {item.images?.length > 0 ? (
                  <TouchableOpacity onPress={() => handleOnShowImagesModal(item.images)}>
                    <MaterialIcons name={item.images.length === 1 ? "photo" : "photo-library"} size={24} color={PRIMARY_COLOUR}/>
                  </TouchableOpacity>
                ) : (
                  <Text>None</Text>
                )}
              </CoreTable.Cell>
            );
          case 'Change Status':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>
                { canEdit &&
                <TouchableOpacity onPress={() => toggleTicketStatus(item)}>
                  {item.status === "Open" ? (
                    <Entypo name="circle-with-cross" size={24} color="black" />
                  ) : (
                    <Entypo name="cycle" size={24} color="black" />
                  )}
                </TouchableOpacity>
                }
                
              </CoreTable.Cell>
            );
          case 'Updates':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex , ...styles.header }}>
                <TouchableOpacity onPress={() => {setUpdateModalOpen(true); setSelectedTicket(item)}}>
                  <MaterialCommunityIcons name="view-list-outline" size={24} color="black" />
                </TouchableOpacity>
              </CoreTable.Cell>
            );
          default:
            return null;
        }
      })}
    </CoreTable.Row>
  )}
  filters={filter.filters}
  setFilter={filter.setFilter}
/>


        
        <CreateTicketModal
          ticketId={selectedTicketId}
          open={createTicketModalOpen}
          onClose={() => {
            setCreateTicketModalOpen(false);
            setSelectedTicketId(undefined)
          }}
        />
      </TablePageLayout>

      <WorkOrderManageModal
        open={workOrderModalVisible}
        onClose={() => setWorkOrderModalVisible(false)}
        onSuccess={() => setWorkOrderModalVisible(false)}
        ticketId={selectedTicketId}
        assetOption={selectedAsset}
      />


      <ColumnExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onConfirm={(cols) => {
          exportExcel({
            ...filter.getFilterValues(),
            columns: cols,
          });
          setExportModalOpen(false);
        }}
        columns={exportableColumns}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
      />

      <TicketUpdatesListModal
        open={updateModalOpen}
        ticket={selectedTicket}
        onClose={() => {setSelectedTicket(undefined); setUpdateModalOpen(false)}} />

    </WebPageLayout>
  );
}

const styles = StyleSheet.create({

  header: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "flex-start",
    paddingHorizontal: 0
  },

});